import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import { analytics } from 'common/services';
import { getItemsCountLabel } from 'common/utils';
import { selectActiveCommunityId } from 'community/state/selectors';
import i18n from 'i18n';
import { addAppConfirmationDialog, addAppSuccessMessage } from 'modules/alerts/state';
import { isApiResponse } from 'modules/api/utils';
import { AppRoutes } from 'modules/router';
import { push } from 'redux-first-history';
import { handleQueryError } from 'store/utils';
import { userApi } from 'user/api/userApi';

import { communityActions } from './actions';
import { communityApi } from './communityApi';
import { registerToCommunitySuccessEffect } from './effects';

import type { Effect, RootState } from 'store';

export const communityApiListener = createListenerMiddleware<RootState>();

const handleApiErrors =
  (defaultMessage = 'Unexpected error occurred'): Effect =>
  ({ type, payload }, { dispatch }) => {
    const response = isApiResponse(payload) ? payload : undefined;
    handleQueryError(payload, dispatch, type, response?.data || defaultMessage);
  };

communityApiListener.startListening({
  matcher: communityApi.endpoints.createCommunity.matchRejected,
  effect: handleApiErrors('Unable to create Community'),
});

communityApiListener.startListening({
  matcher: communityApi.endpoints.removeCommunityMember.matchRejected,
  effect: handleApiErrors('Unable to remove Community Member'),
});

communityApiListener.startListening({
  matcher: communityApi.endpoints.toggleCommunityAdmin.matchRejected,
  effect: handleApiErrors('Unable to update Community Member role'),
});

communityApiListener.startListening({
  actionCreator: communityActions.community.registeredToCommunity,
  effect: registerToCommunitySuccessEffect,
});

// TODO: remove below code and uncomment line 308 from communityApi.ts when BE fixed
communityApiListener.startListening({
  matcher: communityApi.endpoints.submitCronofyCode.matchFulfilled,
  effect: (_args, { dispatch, getState }) => {
    const {
      community: { activeCommunityId },
    } = getState();

    if (activeCommunityId) {
      dispatch(
        communityApi.util.updateQueryData('getCurrentCommunityMember', { communityId: activeCommunityId }, (data) => {
          data.hasCronofyCredentials = true;
        })
      );
    }
  },
});

communityApiListener.startListening({
  matcher: communityApi.endpoints.getCurrentCommunityMember.matchFulfilled,
  effect: ({ payload, meta }, { getState }) => {
    // Only the active community's role: a fetch for the community just left can land after the
    // switch, and its role would then describe the wrong community.
    if (meta.arg.originalArgs.communityId === selectActiveCommunityId(getState())) {
      analytics.register('community_user_role', payload.communityRole);
    }
  },
});

communityApiListener.startListening({
  matcher: communityApi.endpoints.updateMyCommunity.matchFulfilled,
  effect: (_, { dispatch }) => {
    dispatch(addAppSuccessMessage('Your information has been saved'));
  },
});

communityApiListener.startListening({
  matcher: isAnyOf(
    communityApi.endpoints.updateMyCommunity.matchRejected,
    communityApi.endpoints.uploadLogo.matchRejected,
    communityApi.endpoints.updateCommunityBranding.matchRejected
  ),
  effect: handleApiErrors('Unable to update Community'),
});

communityApiListener.startListening({
  matcher: communityApi.endpoints.inviteGuestsToCommunity.matchFulfilled,
  effect: (_, { dispatch }) => {
    dispatch(addAppSuccessMessage('Invitation has been sent'));
  },
});

communityApiListener.startListening({
  matcher: communityApi.endpoints.inviteGuestsToCommunity.matchRejected,
  effect: handleApiErrors('Unable to invite to the Community'),
});

communityApiListener.startListening({
  matcher: communityApi.endpoints.toggleCommunityAdmin.matchFulfilled,
  effect: ({ meta }, { dispatch }) => {
    const { isPromote, displayName } = meta.arg.originalArgs;
    if (isPromote) {
      dispatch(
        addAppConfirmationDialog({
          title: i18n.t('community:promoteToAdmin.successMessage', { displayName: displayName || 'User' }),
          description: '',
          confirmLabel: i18n.t('community:promoteToAdmin.confirmLabel'),
        })
      );
    } else {
      dispatch(
        addAppConfirmationDialog({
          title: i18n.t('community:demoteFromAdmin.successMessage', { displayName: displayName || 'User' }),
          description: '',
          confirmLabel: i18n.t('community:demoteFromAdmin.confirmLabel'),
        })
      );
    }
  },
});

communityApiListener.startListening({
  matcher: communityApi.endpoints.removeCommunityMember.matchFulfilled,
  effect: ({ meta }, { dispatch }) => {
    const { displayName } = meta.arg.originalArgs;
    dispatch(
      addAppConfirmationDialog({
        title: `${displayName || 'User'} has been successfully removed from this Community`,
        description: '',
        confirmLabel: 'Got it',
      })
    );
  },
});

communityApiListener.startListening({
  matcher: communityApi.endpoints.resendInvitation.matchFulfilled,
  effect: (_, { dispatch }) => {
    dispatch(addAppSuccessMessage(i18n.t('community:resendInvitation.successMessage')));
  },
});

communityApiListener.startListening({
  matcher: communityApi.endpoints.resendInvitation.matchRejected,
  effect: handleApiErrors('Unable to resend invitation'),
});

communityApiListener.startListening({
  matcher: communityApi.endpoints.updateCommunityBranding.matchFulfilled,
  effect: (_, { dispatch }) => {
    dispatch(push(AppRoutes.Settings));
  },
});

communityApiListener.startListening({
  matcher: communityApi.endpoints.inviteGuestsToCommunity.matchFulfilled,
  effect: ({ meta }, { dispatch }) => {
    const { guests } = meta.arg.originalArgs;
    dispatch(
      addAppConfirmationDialog({
        title: 'Invitation has been sent',
        description: `You have invited ${getItemsCountLabel('new Member', guests.length)} to your Community.`,
        confirmLabel: 'Understood',
        maxWidth: 'sm',
      })
    );
  },
});

// Member payloads carry the account's job title and socials, so they are stale after a profile save.
communityApiListener.startListening({
  matcher: userApi.endpoints.updateAccountProfile.matchFulfilled,
  effect: (_, { dispatch }) => {
    dispatch(communityApi.util.invalidateTags(['COMMUNITY_MEMBER', 'ALL_COMMUNITY_MEMBERS']));
  },
});
